import React, { useEffect, useState } from 'react';
import apiClient from '../services/apiClient';

const initialEvent = { name: '', sceneId: '', startsAt: '', endsAt: '', priority: 0, enabled: true };
export const toKstInput = value => value ? new Date(new Date(value).getTime() + 9 * 3600000).toISOString().slice(0, 16) : '';
export const fromKstInput = value => new Date(`${value}:00+09:00`).toISOString();

export default function AdminSceneSelectionPanel() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(initialEvent);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [userId, setUserId] = useState('');
  const [trigger, setTrigger] = useState('practice.before.coaching');
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => apiClient.getAdminSceneEvents()).then(result => {
      if (!cancelled) setEvents(result?.events || []);
    }).catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);
  const update = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const save = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      await apiClient.saveAdminSceneEvent(editing?.id, {
        ...form, startsAt: fromKstInput(form.startsAt), endsAt: fromKstInput(form.endsAt),
        ...(editing ? { version: editing.version } : {}),
      });
      setEvents((await apiClient.getAdminSceneEvents()).events);
      setEditing(null); setForm(initialEvent);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const inspect = async event => {
    event.preventDefault(); setBusy(true); setError(''); setPreview(null);
    try { setPreview(await apiClient.previewAdminSceneSelection({ userId, trigger, timezone: 'Asia/Seoul' })); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return <section className="admin-panel admin-scene-selection">
    <h2>Scene events / 이벤트 노출</h2>
    <p>한국 시간(KST) 기준 · 이벤트당 계정별 한 번 · 대상 조건과 재등장 제한을 준수합니다.</p>
    {error && <p role="alert">{error}</p>}
    <form onSubmit={save} className="admin-filters">
      <label>이벤트 이름<input aria-label="Event name" required maxLength={120} value={form.name} onChange={e => update('name', e.target.value)} /></label>
      <label>Scene ID<input aria-label="Event Scene ID" required value={form.sceneId} onChange={e => update('sceneId', e.target.value)} /></label>
      <label>시작 (KST)<input aria-label="Event start KST" type="datetime-local" required value={form.startsAt} onChange={e => update('startsAt', e.target.value)} /></label>
      <label>종료 (KST)<input aria-label="Event end KST" type="datetime-local" required value={form.endsAt} onChange={e => update('endsAt', e.target.value)} /></label>
      <label>우선순위<input aria-label="Event priority" type="number" min="0" max="100" required value={form.priority} onChange={e => update('priority', Number(e.target.value))} /></label>
      <label><input type="checkbox" checked={form.enabled} onChange={e => update('enabled', e.target.checked)} /> 활성</label>
      <button disabled={busy} type="submit">{editing ? '이벤트 수정' : '이벤트 추가'}</button>
      {editing && <button disabled={busy} type="button" onClick={() => { setEditing(null); setForm(initialEvent); }}>취소</button>}
    </form>
    <div className="admin-table-wrap"><table className="admin-user-table">
      <thead><tr><th>이벤트 / Scene</th><th>기간 (KST)</th><th>우선순위</th><th>상태</th><th>설정</th></tr></thead>
      <tbody>{events.map(event => <tr key={event.id}>
        <td>{event.name}<br />{event.sceneId}{event.warning && <p role="status">{event.warning}</p>}</td>
        <td>{toKstInput(event.startsAt).replace('T', ' ')} ~ {toKstInput(event.endsAt).replace('T', ' ')}</td>
        <td>{event.priority}</td><td>{event.enabled ? '활성' : '비활성'}</td>
        <td><button type="button" disabled={busy} onClick={() => {
          setEditing(event); setForm({ name: event.name, sceneId: event.sceneId,
            startsAt: toKstInput(event.startsAt), endsAt: toKstInput(event.endsAt), priority: event.priority, enabled: event.enabled });
        }}>수정</button></td>
      </tr>)}</tbody>
    </table></div>
    <h3>선택 정책 미리보기</h3><p>선택 이력과 이벤트 소진 상태는 변경하지 않습니다.</p>
    <form onSubmit={inspect} className="admin-filters">
      <label>사용자 ID<input required aria-label="Preview user ID" value={userId} onChange={e => setUserId(e.target.value)} /></label>
      <label>Trigger<select aria-label="Preview trigger" value={trigger} onChange={e => setTrigger(e.target.value)}>
        <option value="practice.before.coaching">일반 연습 입장</option><option value="listening.before.coaching">듣기 연습 입장</option>
        <option value="library.before.practice">도서관 간이 연습·듣기 입장</option>
        <option value="vocabulary.before.practice">단어 일반 학습·복습 입장</option>
      </select></label><button disabled={busy} type="submit">미리보기</button>
    </form>
    {preview && <div role="status">
      <p>레벨 {preview.context.level} · 가입 {preview.context.accountAgeDays ?? '?'}일 · 최근 7일 학습 {preview.context.activeDays7}일 · 학습 공백 {preview.context.inactiveDays ?? '?'}일</p>
      <p>우선 이벤트: {preview.priorityEvents.map(event => event.name).join(', ') || '없음'}</p>
      <div className="admin-table-wrap"><table className="admin-user-table">
        <thead><tr><th>Scene</th><th>챕터 구분</th><th>다음 챕터 복습 (KST)</th><th>일반 후보</th><th>가중치</th><th>제외/예약 사유</th><th>재등장 가능 (KST)</th></tr></thead>
        <tbody>{preview.candidates.map((item, index) => <tr key={`${item.locale}:${item.sceneId}:${index}`}>
          <td>{item.sceneId} ({item.locale || 'legacy'})</td>
          <td>{item.chapterId ? `${item.chapterId} · ${{ current: '주력', review: '복습', future: '미도달', invalid: '설정 오류' }[item.chapterRole] || item.chapterRole}` : '—'}</td>
          <td>{item.chapterRole === 'review' ? (item.chapterAvailableAt ? toKstInput(item.chapterAvailableAt).replace('T', ' ') : '지금 가능') : '—'}</td>
          <td>{item.ordinaryCandidate ? '✓' : '—'}</td><td>{item.weight}</td>
          <td>{item.reasons.join(', ') || '—'}</td><td>{item.availableAt ? toKstInput(item.availableAt).replace('T', ' ') : item.reasons.includes('no-repeat') ? '재등장 금지' : '—'}</td>
        </tr>)}</tbody>
      </table></div>
    </div>}
  </section>;
}
